import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import WOService from "src/Services/API/wo.service";
import { formatDate, getRealFileName, getUploadUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import TagInput from "src/Pages/WO/Components/forms/components/TagInput";
import {
  DrawerShell,
  FieldLabel,
  OutlineButton,
  PrimaryButton,
  SelectDropdown,
  ServiceTicketToggle,
} from "./uploadModalUI";

const DOCUMENT_TYPE_OPTIONS = ["Manual", "Spec Sheet", "Drawing", "Warranty", "Other"];

const isVideoFile = (fileName) => !!fileName && /\.(mp4|mov|webm|avi|mkv|m4v|3gp)$/i.test(fileName);
const isPdfFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".pdf");

const getUploaderName = (attachment) =>
  attachment?.uploadedBy?.name ||
  attachment?.uploadedByCustomerUser?.customer?.companyName ||
  attachment?.uploadedByCustomerUser?.account?.name ||
  "-";

const PdfThumb = () => (
  <div className="w-full h-full flex items-center justify-center bg-[#FEF2F2]">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M14 3H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7l-4-4Z" fill="#EF4444" />
      <path d="M14 3v4h4" fill="#FCA5A5" />
    </svg>
  </div>
);

/**
 * "Edit Details" drawer for an existing WoAttachment. Edits the fields the
 * backend's updateAttachment persists: documentName, tags, description,
 * includeInServiceTicket, and (for technical documents) documentType.
 *
 * `kind` is "document" or "photo" — documents get the Document Type field;
 * photos render a thumbnail preview and hide the service-ticket toggle for
 * videos (videos can't be included). `onSaved` lets the parent refresh the WO;
 * the drawer closes itself on success.
 */
const EditAttachmentModal = ({ shown, attachment, kind = "document", onClose, onSaved }) => {
  const [documentName, setDocumentName] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState("");
  const [includeInServiceTicket, setIncludeInServiceTicket] = useState(false);
  const [saving, setSaving] = useState(false);

  // Retain the last attachment so the drawer keeps rendering its content while
  // it slides out (the parent clears `attachment` the moment it closes).
  const lastAttachmentRef = useRef(attachment);
  if (attachment) lastAttachmentRef.current = attachment;
  const current = attachment || lastAttachmentRef.current;

  const isDocument = kind === "document";
  const isVideo = isVideoFile(current?.fileName);
  const isPdf = isPdfFile(current?.fileName);

  useEffect(() => {
    if (!shown || !attachment) return;
    setDocumentName(attachment.documentName || getRealFileName(attachment.fileName) || attachment.fileName || "");
    setDocumentType(attachment.documentType || "");
    setTags(Array.isArray(attachment.tags) ? attachment.tags : []);
    setDescription(attachment.description || "");
    setIncludeInServiceTicket(!!attachment.includeInServiceTicket);
    setSaving(false);
  }, [shown, attachment]);

  if (!current) return null;

  const previewUrl = current.fileName ? getUploadUrl(current.fileName) : "";

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        documentName: documentName.trim() || getRealFileName(current.fileName) || current.fileName,
        tags,
        description: description.trim(),
        includeInServiceTicket: isVideo ? false : includeInServiceTicket,
      };
      if (isDocument) data.documentType = documentType || "";
      const res = await WOService.updateAttachment({ id: current.id, data });
      if (res.status === 200 || res.status === 201) {
        toast("Changes saved");
        onSaved?.();
        onClose?.();
      } else {
        toast("Something went wrong");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      shown={shown}
      title="Edit Details"
      onClose={onClose}
      footer={
        <div className="w-full grid grid-cols-2 gap-3">
          <OutlineButton onClick={onClose} disabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton onClick={handleSave} loading={saving} loadingLabel="Saving...">
            Save Changes
          </PrimaryButton>
        </div>
      }
    >
      <div className="p-6 flex flex-col gap-6">
        {/* File preview row */}
        <div className="flex items-center gap-4">
          <div className="w-[113px] h-[71px] shrink-0 rounded-[8px] overflow-hidden bg-[#F3F4F6] border border-[#E5E8F3]">
            {isVideo ? (
              <video src={previewUrl} className="w-full h-full object-cover" muted preload="metadata" />
            ) : isPdf ? (
              <PdfThumb />
            ) : (
              <img src={previewUrl} alt={documentName} className="w-full h-full object-cover" />
            )}
          </div>
          <div className="min-w-0 flex flex-col gap-1">
            <span
              className="font-OpenSansSemiBold text-[16px] leading-[24px] text-[#1A2332] truncate"
              title={current.documentName || current.fileName}
            >
              {current.documentName || getRealFileName(current.fileName) || current.fileName}
            </span>
            <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
              Uploaded by {getUploaderName(current)}
              {current.createdAt ? ` • ${formatDate(current.createdAt, "MMM D")}` : ""}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <FieldLabel>File Name</FieldLabel>
          <input
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            className="h-[44px] px-3 border border-[#E5E8F3] rounded-[6px] outline-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
          />
        </div>

        {isDocument && (
          <div className="flex flex-col gap-1">
            <FieldLabel>Document Type</FieldLabel>
            <SelectDropdown
              value={documentType}
              options={DOCUMENT_TYPE_OPTIONS}
              placeholder="Select document type"
              onChange={setDocumentType}
            />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <FieldLabel>Tag</FieldLabel>
          <TagInput selectedTags={tags} onChange={setTags} />
        </div>

        <div className="flex flex-col gap-1">
          <FieldLabel>Description (Optional)</FieldLabel>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add notes..."
            rows={3}
            className="h-[112px] p-3 bg-[#FBFCFF] border border-[#E5E8F3] rounded-[6px] outline-none resize-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
          />
        </div>

        <ServiceTicketToggle
          checked={!isVideo && includeInServiceTicket}
          disabled={isVideo}
          onChange={() => !isVideo && setIncludeInServiceTicket((prev) => !prev)}
          label="Include In Service Ticket"
          helper={
            isVideo
              ? "Videos can't be included in the service ticket."
              : "If enabled, this file will be available when generating the Service Ticket."
          }
        />
      </div>
    </DrawerShell>
  );
};

export default EditAttachmentModal;
